<template>
  <div class="customer-profile-page">
    <div class="customer-profile-shell">
      <section class="customer-hero">
        <v-btn
          icon
          class="customer-hero__back"
          title="Zurück zur Benutzersuche"
          aria-label="Zurück zur Benutzersuche"
          @click="$router.push('/dashboard/user/search')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <div class="customer-hero__identity">
          <v-avatar :size="isMobile ? 72 : 88" color="#26322b" class="customer-hero__avatar">
            <img v-if="user._id" :src="profileImage" :alt="fullName" />
            <v-icon v-else size="36">mdi-account-outline</v-icon>
          </v-avatar>
          <div class="customer-hero__copy">
            <span class="customer-eyebrow">KUNDENAKTE</span>
            <h1>{{ user._id ? fullName : "Kundenprofil wird geladen" }}</h1>
            <div v-if="user._id" class="customer-hero__meta">
              <span v-if="user.email">
                <v-icon small>mdi-email-outline</v-icon>
                {{ user.email }}
              </span>
              <span v-if="user.telefon">
                <v-icon small>mdi-phone-outline</v-icon>
                {{ user.telefon }}
              </span>
              <span v-if="tacMembershipStart">
                <v-icon small>mdi-account-clock-outline</v-icon>
                Mitglied seit {{ $moment(tacMembershipStart).format("MM.YYYY") }}
              </span>
            </div>
            <div v-if="user._id" class="customer-hero__badges">
              <span :class="{ 'identity-badge--success': user.verifiedEmail }" class="identity-badge">
                <v-icon x-small>{{ user.verifiedEmail ? "mdi-check-decagram" : "mdi-email-alert-outline" }}</v-icon>
                {{ user.verifiedEmail ? "E-Mail verifiziert" : "E-Mail nicht verifiziert" }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="user._id" class="customer-hero__actions">
          <v-btn v-if="user.telefon" icon :href="`tel:${user.telefon}`" title="Kunden anrufen" aria-label="Kunden anrufen">
            <v-icon>mdi-phone-outline</v-icon>
          </v-btn>
          <v-btn v-if="user.email" icon :href="`mailto:${user.email}`" title="E-Mail schreiben" aria-label="E-Mail schreiben">
            <v-icon>mdi-email-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="userService.hasRight('u_delete')"
            icon
            color="error"
            title="Benutzer löschen"
            aria-label="Benutzer löschen"
            @click="deleteUser">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </section>

      <nav class="customer-navigation" aria-label="Bereiche der Kundenakte">
        <button
          v-for="category in categories"
          :key="category.value"
          type="button"
          class="customer-navigation__item"
          :class="{ 'customer-navigation__item--active': activeGroup === category.value }"
          @click="activeGroup = category.value">
          <v-icon small>{{ category.icon }}</v-icon>
          <span>{{ category.text }}</span>
        </button>
      </nav>

      <main class="customer-content">
        <div v-if="loadingProfile" class="customer-loading">
          <v-progress-circular :size="56" :width="6" color="primary" indeterminate />
          <strong>Kundenakte wird geladen</strong>
        </div>

        <template v-else-if="user._id">
          <user-dashboard-customer-care v-if="activeGroup === 'care'" :user="user" />

          <section v-if="activeGroup === 'infos'" class="legacy-section-shell">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">STAMMDATEN &amp; ZUGRIFF</span>
                <h2>Persönliche Angaben</h2>
                <p>Kontaktinformationen, Adresse und – sofern berechtigt – Rollen und Einzelrechte.</p>
              </div>
              <v-icon color="#55ca82">mdi-account-edit-outline</v-icon>
            </header>
            <user-dashboard-customer-infos :is-mobile="isMobile" :user="user" />
          </section>

          <section v-if="activeGroup === 'contracts'" class="legacy-section-shell legacy-section-shell--open">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">MITGLIEDSCHAFT</span>
                <h2>Verträge &amp; Pakete</h2>
                <p>Bestehende Vertragsfunktionen und deren Rechteprüfungen bleiben unverändert erhalten.</p>
              </div>
              <v-icon color="#55ca82">mdi-file-sign</v-icon>
            </header>
            <user-dashboard-contracts :is-mobile="isMobile" :user="user" @refetchUser="refetchUser" />
          </section>

          <section v-if="activeGroup === 'interactions'" class="legacy-section-shell legacy-section-shell--open">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">KONTAKTHISTORIE</span>
                <h2>Dokumentierte Interaktionen</h2>
                <p>Notizen und Kontakte ausserhalb eines konkreten WorkCalendar-Termins.</p>
              </div>
              <v-icon color="#55ca82">mdi-message-text-clock-outline</v-icon>
            </header>
            <user-dashboard-interactions :user="user" />
          </section>

          <section v-if="activeGroup === 'membercard'" class="legacy-section-shell legacy-section-shell--open">
            <header class="section-intro">
              <div>
                <span class="customer-eyebrow">EINSTIEG &amp; LEAD</span>
                <h2>11-Tage-Mitgliedskarte</h2>
                <p>Aktivierung, Check-ins und Lead-Verknüpfung der Mitgliedskarte.</p>
              </div>
              <v-icon color="#55ca82">mdi-card-account-details-star-outline</v-icon>
            </header>
            <user-dashboard-membercard :user="user" />
          </section>
        </template>

        <div v-else class="customer-loading customer-loading--error">
          <v-icon color="error" size="36">mdi-account-alert-outline</v-icon>
          <strong>Kundenakte konnte nicht geladen werden</strong>
          <v-btn color="primary" depressed class="text-none" @click="refetchUser">Erneut versuchen</v-btn>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"

import userDashboardCustomerCare from "./userDashboardCustomerCare"
import userDashboardCustomerInfos from "./userDashboardCustomerInfos"
import userDashboardMembercard from "./userDashboardMembercard"
import userDashboardContracts from "./userDashboardContracts"
import userDashboardInteractions from "./userDashboardInteractions"

export default {
  name: "UserDashboardCustomer",
  components: {
    userDashboardCustomerCare,
    userDashboardCustomerInfos,
    userDashboardMembercard,
    userDashboardContracts,
    userDashboardInteractions,
  },
  data() {
    return {
      baseCategories: [
        { text: "Betreuung", value: "care", icon: "mdi-account-heart-outline" },
        { text: "Stammdaten", value: "infos", icon: "mdi-account-details-outline" },
        { text: "Verträge", value: "contracts", icon: "mdi-file-sign" },
        { text: "Interaktionen", value: "interactions", icon: "mdi-message-text-clock-outline", right: "tp_create" },
        { text: "11-Tage-Karte", value: "membercard", icon: "mdi-card-account-details-star-outline" },
      ],
      activeGroup: "care",
      userService,
      user: {},
      tacContractInfos: [],
      loadingProfile: false,
    }
  },
  computed: {
    categories() {
      return this.baseCategories.filter((category) => {
        if (category.right && !this.userService.hasRight(category.right)) return false
        if (category.value === "care" && this.user.hasInternalAccess) return false
        return true
      })
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    fullName() {
      const name = `${this.user.vorname || ""} ${this.user.nachname || ""}`.trim()
      return name || this.user.email || "Unbekannter Benutzer"
    },
    profileImage() {
      if (this.user.bild) return `/img/profilbilder${this.user.bild}`
      return `https://app.wellcomefit.ch/api/checkin/pool/images/${this.user._id}`
    },
    tacMembershipStart() {
      const contracts = Array.isArray(this.tacContractInfos) ? this.tacContractInfos : []
      const memberships = contracts.filter((contract) => {
        const type = String(contract && contract.CONTRACTTYP ? contract.CONTRACTTYP : "")
          .trim()
          .toLocaleLowerCase()
        const group = String(contract && contract.GROUP_NAME ? contract.GROUP_NAME : "")
          .trim()
          .toLocaleLowerCase()

        return type === "hauptvertrag" || (type === "bonusvertrag" && group !== "zusatzpakete")
      })
      const candidates = memberships.length ? memberships : contracts
      const validDates = candidates.map((contract) => this.$moment(contract && contract.TERM_STARTDATE)).filter((date) => date.isValid())

      if (!validDates.length) return null
      return validDates.reduce((earliest, date) => (date.isBefore(earliest) ? date : earliest))
    },
    membercardStatus() {
      const membercard = this.user && this.user.membercard
      if (!membercard || !membercard.startDate) return null

      const rawEndDate = membercard.endDate
      const endDate = rawEndDate ? this.$moment(rawEndDate) : null
      if (!endDate || !endDate.isValid()) {
        return {
          label: "Mitgliedskarte prüfen",
          tone: "warning",
          icon: "mdi-card-alert-outline",
        }
      }

      if (/^\d{4}-\d{2}-\d{2}$/.test(String(rawEndDate))) endDate.endOf("day")
      if (endDate.isBefore(this.$moment())) {
        return {
          label: "Mitgliedskarte abgelaufen",
          tone: "expired",
          icon: "mdi-card-off-outline",
        }
      }

      if (!membercard.isActive) {
        return {
          label: "Mitgliedskarte pausiert",
          tone: "muted",
          icon: "mdi-pause-circle-outline",
        }
      }

      return {
        label: "Mitgliedskarte aktiv",
        tone: "accent",
        icon: "mdi-card-account-details-star-outline",
      }
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(id) {
        if (id) {
          this.activeGroup = "care"
          this.fetchUser(id)
        }
      },
    },
  },
  methods: {
    refetchUser() {
      if (this.$route.params.id) this.fetchUser(this.$route.params.id)
    },
    async fetchUser(id) {
      this.loadingProfile = true
      this.tacContractInfos = []
      try {
        this.user = await api.getCustomerDashboard(id)
        if (this.user.hasInternalAccess && this.activeGroup === "care") this.activeGroup = "infos"
        this.fetchTacMembership(id)
      } catch (error) {
        this.user = {}
        this.$toast.error("Kundenakte konnte nicht geladen werden")
      } finally {
        this.loadingProfile = false
      }
    },
    async fetchTacMembership(id) {
      try {
        const contracts = await api.fetchTACContractDetails(id)
        if (String(this.$route.params.id || "") !== String(id || "")) return
        this.tacContractInfos = Array.isArray(contracts) ? contracts : []
      } catch (error) {
        if (String(this.$route.params.id || "") === String(id || "")) this.tacContractInfos = []
      }
    },
    async deleteUser() {
      if (!this.userService.hasRight("u_delete")) return
      const confirmed = window.confirm(
        `Soll der Benutzer ${this.fullName} wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.`,
      )
      if (!confirmed || !window.confirm("Ganz sicher?")) return

      try {
        const result = await api.deleteUser({ benutzer_id: this.user._id })
        if (!result || !result.status) throw new Error("delete_failed")
        this.$toast.success("Benutzer wurde gelöscht")
        this.$router.push("/dashboard/user/search")
      } catch (error) {
        this.$toast.error("Benutzer konnte nicht gelöscht werden")
      }
    },
  },
}
</script>

<style scoped>
.customer-profile-page {
  --cp-primary: #009640;
  --cp-primary-soft: rgba(0, 150, 64, 0.14);
  --cp-surface: rgba(0, 0, 0, 0.2);
  --cp-surface-raised: rgba(255, 255, 255, 0.045);
  --cp-border: rgba(255, 255, 255, 0.1);
  --cp-text: #f6f7f6;
  --cp-muted: #a6aaa7;
  min-height: 100%;
  padding: clamp(14px, 2.4vw, 34px);
  color: var(--cp-text);
  background: transparent;
}

.customer-profile-shell {
  width: 100%;
  max-width: 1540px;
  margin: 0 auto;
}

.customer-hero {
  position: relative;
  display: flex;
  min-height: 164px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 30px 26px 82px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 25px;
  background: radial-gradient(circle at 92% 3%, rgba(0, 150, 64, 0.16), transparent 34%),
    linear-gradient(110deg, rgba(0, 0, 0, 0.32), rgba(255, 255, 255, 0.025)), var(--cp-surface);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.23);
  backdrop-filter: blur(18px);
}

.customer-hero::after {
  position: absolute;
  top: -148px;
  right: -52px;
  width: 360px;
  height: 360px;
  border: 1px solid rgba(0, 150, 64, 0.13);
  border-radius: 50%;
  content: "";
  pointer-events: none;
}

.customer-hero__back {
  position: absolute;
  z-index: 2;
  top: 24px;
  left: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
}

.customer-hero__back .v-icon {
  color: #d9dedb !important;
}

.customer-hero__identity {
  z-index: 1;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 20px;
}

.customer-hero__avatar {
  flex: 0 0 auto;
  border: 3px solid rgba(101, 202, 139, 0.32);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.3);
}

.customer-hero__avatar img {
  object-fit: cover;
}

.customer-hero__copy {
  min-width: 0;
}

.customer-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #65ca8b;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.13em;
  line-height: 1.2;
}

.customer-hero h1 {
  overflow: hidden;
  margin: 0;
  color: #ffffff;
  font-size: clamp(28px, 3.1vw, 42px);
  font-weight: 780;
  letter-spacing: -0.04em;
  line-height: 1.08;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-hero__meta,
.customer-hero__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 10px;
}

.customer-hero__meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #b8bcb9;
  font-size: 13px;
}

.customer-hero__meta .v-icon {
  color: #aeb5b0 !important;
}

.identity-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  color: #b4b9b5;
  background: rgba(255, 255, 255, 0.035);
  font-size: 12px;
  font-weight: 700;
}

.identity-badge .v-icon {
  color: inherit !important;
}

.identity-badge--success {
  border-color: rgba(0, 150, 64, 0.27);
  color: #7fd7a1;
  background: rgba(0, 150, 64, 0.1);
}

.identity-badge--accent {
  border-color: rgba(37, 135, 232, 0.3);
  color: #78baf6;
  background: rgba(37, 135, 232, 0.1);
}

.identity-badge--expired {
  border-color: rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
}

.identity-badge--warning {
  border-color: rgba(245, 158, 11, 0.3);
  color: #f3c568;
  background: rgba(245, 158, 11, 0.1);
}

.identity-badge--muted {
  border-color: rgba(148, 163, 184, 0.23);
  color: #bdc4cb;
  background: rgba(148, 163, 184, 0.09);
}

.customer-hero__actions {
  z-index: 1;
  display: flex;
  flex: 0 0 auto;
  gap: 7px;
}

.customer-hero__actions .v-btn {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.045);
}

.customer-hero__actions .v-btn .v-icon {
  color: #d9dedb !important;
}

.customer-hero__actions .v-btn.error--text .v-icon {
  color: #ff6b72 !important;
}

.customer-navigation {
  display: flex;
  margin-top: 15px;
  padding: 7px;
  overflow-x: auto;
  border: 1px solid var(--cp-border);
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.18);
  scrollbar-width: thin;
}

.customer-navigation__item {
  position: relative;
  display: inline-flex;
  min-height: 44px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 17px;
  border: 0;
  border-radius: 12px;
  color: #9fa5a1;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 750;
  transition: color 160ms ease, background 160ms ease;
}

.customer-navigation__item .v-icon {
  color: #a7ada9 !important;
}

.customer-navigation__item:hover {
  color: #d9dcda;
  background: rgba(255, 255, 255, 0.035);
}

.customer-navigation__item--active {
  color: #81d9a3;
  background: var(--cp-primary-soft);
}

.customer-navigation__item--active .v-icon {
  color: #65ca8b !important;
}

.customer-content {
  margin-top: 15px;
}

.customer-loading {
  display: flex;
  min-height: 420px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  border: 1px solid var(--cp-border);
  border-radius: 22px;
  background: var(--cp-surface);
}

.customer-loading strong {
  font-size: 16px;
}

.customer-loading--error {
  color: #fca5a5;
}

.legacy-section-shell {
  overflow: hidden;
  padding: 24px;
  border: 1px solid var(--cp-border);
  border-radius: 22px;
  background: linear-gradient(145deg, var(--cp-surface-raised), rgba(0, 0, 0, 0.17));
}

.legacy-section-shell--open {
  padding: 0 0 24px;
}

.section-intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: -24px -24px 20px;
  padding: 24px;
  border-bottom: 1px solid var(--cp-border);
  background: rgba(255, 255, 255, 0.018);
}

.legacy-section-shell--open .section-intro {
  margin: 0 0 4px;
}

.section-intro h2 {
  margin: 0;
  color: #ffffff;
  font-size: 23px;
  letter-spacing: -0.03em;
}

.section-intro p {
  margin: 6px 0 0;
  color: var(--cp-muted);
  font-size: 13px;
}

.section-intro > .v-icon {
  color: #65ca8b !important;
}

.legacy-section-shell ::v-deep .v-card:not(.contract-card) {
  color: #f6f7f6;
  background: transparent !important;
  box-shadow: none !important;
}

.legacy-section-shell ::v-deep .v-data-table,
.legacy-section-shell ::v-deep .v-sheet:not(.contract-card) {
  color: #f6f7f6;
  background: transparent !important;
}

.legacy-section-shell ::v-deep .v-input,
.legacy-section-shell ::v-deep .v-label,
.legacy-section-shell ::v-deep .v-card__text,
.legacy-section-shell ::v-deep .v-card__subtitle,
.legacy-section-shell ::v-deep td,
.legacy-section-shell ::v-deep th {
  font-size: 12px !important;
}

@media (max-width: 860px) {
  .customer-hero {
    align-items: flex-start;
    padding: 72px 20px 22px;
  }

  .customer-hero__identity {
    align-items: flex-start;
  }

  .customer-hero h1 {
    font-size: 27px;
    white-space: normal;
  }

  .customer-hero__meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
  }

  .customer-hero__actions {
    position: absolute;
    top: 20px;
    right: 18px;
  }
}

@media (max-width: 600px) {
  .customer-profile-page {
    padding: 10px;
  }

  .customer-hero {
    min-height: 0;
    padding: 76px 18px 22px;
  }

  .customer-hero__identity {
    width: 100%;
    align-items: flex-start;
    gap: 14px;
  }

  .customer-hero__avatar {
    position: static;
    margin-top: 2px;
  }

  .customer-hero__copy {
    min-width: 0;
    width: 100%;
  }

  .customer-hero h1 {
    font-size: 24px;
    line-height: 1.12;
  }

  .customer-hero__meta span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .customer-navigation__item {
    min-height: 42px;
    padding: 0 13px;
  }

  .legacy-section-shell {
    padding: 16px;
  }

  .section-intro {
    margin: -16px -16px 16px;
    padding: 18px 16px;
  }
}
</style>
